import { Outlet, Link, useLocation } from 'react-router-dom'
import { useState } from 'react'

const KIOSK_URL = 'https://pos-frontend-kappa-two.vercel.app/?mode=kiosk'

export default function Layout() {
  const location = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/menu', label: 'Menu' },
    { path: '/about', label: 'About' },
    { path: '/contact', label: 'Contact' },
  ]

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname.startsWith(path)
  }

  return (
    <div className="min-h-screen flex flex-col bg-black">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black/95 backdrop-blur border-b-2 border-yellow-500">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-32">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3">
              <img
                src="https://pineapple-pos-backend-production.up.railway.app/uploads/menu/hanky-panky-logo.png"
                alt="Hanky Panky Burgers"
                className="h-20 w-auto"
                onError={(e) => { e.target.style.display = 'none' }}
              />
              <span className="text-3xl font-bold text-yellow-500 hidden sm:block">
                Late Night Smash Burgers
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-xl font-bold transition-colors ${
                    isActive(link.path)
                      ? 'text-yellow-500'
                      : 'text-white hover:text-yellow-500'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <a
                href={KIOSK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 bg-yellow-500 hover:bg-yellow-400 text-black font-bold text-xl rounded-lg transition-colors"
              >
                Order Now
              </a>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-white"
              aria-label="Toggle menu"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {mobileMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-yellow-500/30">
              <div className="flex flex-col gap-3">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`text-base font-medium px-4 py-2 rounded transition-colors ${
                      isActive(link.path)
                        ? 'text-yellow-500 bg-yellow-500/10'
                        : 'text-white hover:text-yellow-500'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                <a
                  href={KIOSK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileMenuOpen(false)}
                  className="mx-4 mt-2 px-4 py-3 bg-yellow-500 hover:bg-yellow-400 text-black font-bold rounded-lg text-center transition-colors"
                >
                  Order Now
                </a>
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-zinc-900 border-t border-yellow-500/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Brand */}
            <div>
              <h3 className="text-xl font-bold text-yellow-500 mb-4">
                Hanky Panky
              </h3>
              <p className="text-gray-400 text-sm">
                Late night smash burgers in Fayetteville, AR.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-white font-semibold mb-4">Quick Links</h4>
              <div className="flex flex-col gap-2">
                <Link to="/menu" className="text-gray-400 hover:text-yellow-500 text-sm">
                  View Menu
                </Link>
                <a
                  href={KIOSK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-yellow-500 text-sm"
                >
                  Order Online
                </a>
                <Link to="/about" className="text-gray-400 hover:text-yellow-500 text-sm">
                  About Us
                </Link>
                <Link to="/contact" className="text-gray-400 hover:text-yellow-500 text-sm">
                  Contact
                </Link>
              </div>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="text-white font-semibold mb-4">Location</h4>
              <div className="flex flex-col gap-2 text-sm text-gray-400">
                <p>Inside Prelude Breakfast Bar</p>
                <p>509 W. Spring Street, Ste 210</p>
                <p>Fayetteville, AR</p>
                <a
                  href="https://www.google.com/maps/search/?api=1&query=509+W+Spring+Street+Fayetteville+AR"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-yellow-500 hover:text-yellow-400"
                >
                  Get Directions
                </a>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-zinc-800 text-center text-sm text-gray-500">
            <p>&copy; {new Date().getFullYear()} Hanky Panky. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}
